import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Close as CloseIcon, Calculate as CalculateIcon } from '@mui/icons-material';
import { WatchlistEntry, Stock } from '../types';
import { StockService } from '../services/stockService';
import { tradingConfig } from '../config/tradingConfig';
import HelpTooltip from './HelpTooltip';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface EnhancedStockEditDialogProps {
  open: boolean;
  onClose: () => void;
  stock: WatchlistEntry | null;
  onUpdate: (updatedStock: Partial<Stock> & { symbol: string }) => void;
}

const EnhancedStockEditDialog: React.FC<EnhancedStockEditDialogProps> = ({
  open,
  onClose,
  stock,
  onUpdate,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingFieldValues, setLoadingFieldValues] = useState(false);

  // Field states
  const [currentPrice, setCurrentPrice] = useState('');
  const [pivotPrice, setPivotPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [weeklyBaseType, setWeeklyBaseType] = useState('');
  const [weeklyBaseStage, setWeeklyBaseStage] = useState('');
  const [volume, setVolume] = useState('');
  const [avgDailyVolume, setAvgDailyVolume] = useState('');
  const [marketCap, setMarketCap] = useState('');
  const [peRatio, setPeRatio] = useState('');
  const [forwardPeRatio, setForwardPeRatio] = useState('');
  const [compRating, setCompRating] = useState('');
  const [epsRating, setEpsRating] = useState('');
  const [rsRating, setRsRating] = useState('');
  const [adRating, setAdRating] = useState('');
  const [smrRating, setSmrRating] = useState('');
  const [indGroupRank, setIndGroupRank] = useState('');
  const [industryName, setIndustryName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [notes, setNotes] = useState('');

  // Dropdown options
  const [fieldOptions, setFieldOptions] = useState({
    baseTypes: [] as string[],
    baseStages: [] as string[],
    adRatings: [] as string[],
    smrRatings: [] as string[],
    industries: [] as string[],
  });

  useEffect(() => {
    if (stock && open) {
      // Initialize form with stock values
      setCurrentPrice(stock.currentPrice?.toString() || '');
      setPivotPrice(stock.pivotPrice?.toString() || '');
      setTargetPrice(stock.targetPrice?.toString() || '');
      setWeeklyBaseType(stock.weeklyBaseType || '');
      setWeeklyBaseStage(stock.weeklyBaseStage || '');
      setVolume(stock.volume?.toString() || '');
      setAvgDailyVolume(stock.avgDailyVolume?.toString() || '');
      setMarketCap(stock.marketCap?.toString() || '');
      setPeRatio(stock.peRatio?.toString() || '');
      setForwardPeRatio(stock.forwardPeRatio?.toString() || '');
      setCompRating(stock.compRating?.toString() || '');
      setEpsRating(stock.epsRating?.toString() || '');
      setRsRating(stock.rsRating?.toString() || '');
      setAdRating(stock.adRating || '');
      setSmrRating(stock.smrRating || '');
      setIndGroupRank(stock.indGroupRank?.toString() || '');
      setIndustryName(stock.industryName || '');
      setCompanyName(stock.companyName || '');
      setNotes(stock.notes || '');
    }
  }, [stock, open]);

  useEffect(() => {
    if (open && fieldOptions.baseTypes.length === 0) {
      loadFieldValues();
    }
  }, [open]);

  const loadFieldValues = async () => {
    setLoadingFieldValues(true);
    try {
      const values = await StockService.getUniqueFieldValues();
      setFieldOptions(values);
    } catch (error) {
      console.error('Error loading field values:', error);
    }
    setLoadingFieldValues(false);
  };


  const calculateTargetFromPivot = () => {
    if (pivotPrice) {
      const pivot = parseFloat(pivotPrice);
      const target = StockService.calculateTargetPrice(pivot);
      setTargetPrice(target.toFixed(2));
    }
  };

  const handleSave = async () => {
    if (!stock) return;

    setLoading(true);
    setError(null);

    try {
      const updatedData: Partial<Stock> & { symbol: string } = {
        symbol: stock.symbol,
      };

      // Only include fields that have values
      if (currentPrice) updatedData.currentPrice = parseFloat(currentPrice);
      if (pivotPrice) updatedData.pivotPrice = parseFloat(pivotPrice);
      if (targetPrice) updatedData.targetPrice = parseFloat(targetPrice);
      if (weeklyBaseType) updatedData.weeklyBaseType = weeklyBaseType;
      if (weeklyBaseStage) updatedData.weeklyBaseStage = weeklyBaseStage;
      if (volume) updatedData.volume = parseFloat(volume);
      if (avgDailyVolume) updatedData.avgDailyVolume = parseFloat(avgDailyVolume);
      if (marketCap) updatedData.marketCap = parseFloat(marketCap);
      if (peRatio) updatedData.peRatio = parseFloat(peRatio);
      if (forwardPeRatio) updatedData.forwardPeRatio = parseFloat(forwardPeRatio);
      if (compRating) updatedData.compRating = parseInt(compRating);
      if (epsRating) updatedData.epsRating = parseInt(epsRating);
      if (rsRating) updatedData.rsRating = parseInt(rsRating);
      if (adRating) updatedData.adRating = adRating;
      if (smrRating) updatedData.smrRating = smrRating;
      if (indGroupRank) updatedData.indGroupRank = parseInt(indGroupRank);
      if (industryName) updatedData.industryName = industryName;
      if (companyName) updatedData.companyName = companyName;
      // Always save notes (even if empty, to allow clearing)
      updatedData.notes = notes;
      
      // Calculate volume change percentage if we have both volumes
      if (volume && avgDailyVolume) {
        const currentVol = parseFloat(volume);
        const avgVol = parseFloat(avgDailyVolume);
        if (avgVol > 0) {
          updatedData.volumeChgVs50Day = ((currentVol - avgVol) / avgVol) * 100;
        }
      }

      // Save to database
      await StockService.updateWatchlistStock(updatedData);
      
      // Notify parent component
      onUpdate(updatedData);
      onClose();
    } catch (err) {
      setError('Failed to update stock. Please try again.');
      console.error('Error updating stock:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Update Stock - {stock?.symbol}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Alert severity="info" sx={{ mb: 2 }}>
          After updating stock data, refresh the dashboard to see updated recommendations.
        </Alert>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={fullScreen ? "scrollable" : "standard"}
          scrollButtons={fullScreen ? "auto" : false}
        >
          <Tab label="Price & Targets" />
          <Tab label="Technical Data" />
          <Tab label="Ratings" />
          <Tab label="Company Info" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Current Price"
                type="number"
                value={currentPrice}
                onChange={(e) => setCurrentPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pivot Price"
                type="number"
                value={pivotPrice}
                onChange={(e) => setPivotPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpTooltip title="The price level where the stock is expected to find support or resistance" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Target Price"
                type="number"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={calculateTargetFromPivot} size="small">
                        <CalculateIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={`Default: ${tradingConfig.targetPricePercentage}% above pivot`}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Volume"
                type="number"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                helperText="Today's trading volume"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="50-Day Avg Volume"
                type="number"
                value={avgDailyVolume}
                onChange={(e) => setAvgDailyVolume(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpTooltip title="Average daily trading volume over the past 50 days" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Market Cap (millions)"
                type="number"
                value={marketCap}
                onChange={(e) => setMarketCap(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">M</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="P/E Ratio"
                type="number"
                value={peRatio}
                onChange={(e) => setPeRatio(e.target.value)}
                helperText="Price to Earnings"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Forward P/E"
                type="number"
                value={forwardPeRatio}
                onChange={(e) => setForwardPeRatio(e.target.value)}
                helperText="Forward P/E Ratio"
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Base Type</InputLabel>
                <Select
                  value={weeklyBaseType}
                  onChange={(e) => setWeeklyBaseType(e.target.value)}
                  label="Base Type"
                >
                  {loadingFieldValues ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : (
                    fieldOptions.baseTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))
                  )}
                  <MenuItem value="">
                    <em>Other/Custom</em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Base Stage</InputLabel>
                <Select
                  value={weeklyBaseStage}
                  onChange={(e) => setWeeklyBaseStage(e.target.value)}
                  label="Base Stage"
                >
                  {fieldOptions.baseStages.map((stage) => (
                    <MenuItem key={stage} value={stage}>
                      Stage {stage}
                    </MenuItem>
                  ))}
                  <MenuItem value="">
                    <em>Other/Custom</em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Comp Rating"
                type="number"
                value={compRating}
                onChange={(e) => setCompRating(e.target.value)}
                inputProps={{ min: 1, max: 99 }}
                helperText="1-99 (99 is best)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="EPS Rating"
                type="number"
                value={epsRating}
                onChange={(e) => setEpsRating(e.target.value)}
                inputProps={{ min: 1, max: 99 }}
                helperText="1-99 (99 is best)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="RS Rating"
                type="number"
                value={rsRating}
                onChange={(e) => setRsRating(e.target.value)}
                inputProps={{ min: 1, max: 99 }}
                helperText="1-99 (99 is best)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>A/D Rating</InputLabel>
                <Select
                  value={adRating}
                  onChange={(e) => setAdRating(e.target.value)}
                  label="A/D Rating"
                >
                  {fieldOptions.adRatings.map((rating) => (
                    <MenuItem key={rating} value={rating}>
                      {rating}
                    </MenuItem>
                  ))}
                  <MenuItem value="">
                    <em>Other/Custom</em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>SMR Rating</InputLabel>
                <Select
                  value={smrRating}
                  onChange={(e) => setSmrRating(e.target.value)}
                  label="SMR Rating"
                >
                  {fieldOptions.smrRatings.map((rating) => (
                    <MenuItem key={rating} value={rating}>
                      {rating}
                    </MenuItem>
                  ))}
                  <MenuItem value="">
                    <em>Other/Custom</em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={industryName}
                  onChange={(e) => setIndustryName(e.target.value)}
                  label="Industry"
                >
                  {fieldOptions.industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                  <MenuItem value="">
                    <em>Other/Custom</em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Industry Group Rank"
                type="number"
                value={indGroupRank}
                onChange={(e) => setIndGroupRank(e.target.value)}
                helperText="Lower is better (1 = best)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add your notes about this stock (e.g., why it's on your watchlist, key observations, trading strategy)"
                helperText="Personal notes for reference when reviewing recommendations"
              />
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !stock}
        >
          {loading ? <CircularProgress size={24} /> : 'Update Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedStockEditDialog;